import { z } from 'zod';
import { parse } from 'tldts';

// From https://gist.github.com/dperini/729294
const urlRegex = new RegExp(
  '^' +
    // protocol identifier (optional)
    // short syntax // still required
    '(?:(?:(?:https?|ftp):)?\\/\\/)' +
    // user:pass BasicAuth (optional)
    '(?:\\S+(?::\\S*)?@)?' +
    '(?:' +
    // IP address exclusion
    // private & local networks
    '(?!(?:10|127)(?:\\.\\d{1,3}){3})' +
    '(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
    '(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})' +
    // IP address dotted notation octets
    // excludes loopback network 0.0.0.0
    // excludes reserved space >= 224.0.0.0
    // excludes network & broadcast addresses
    // (first & last IP address of each class)
    '(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
    '(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
    '(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
    '|' +
    // host & domain names, may end with dot
    // can be replaced by a shortest alternative
    // (?![-_])(?:[-\\w\\u00a1-\\uffff]{0,63}[^-_]\\.)+
    '(?:' +
    '(?:' +
    '[a-z0-9\\u00a1-\\uffff]' +
    '[a-z0-9\\u00a1-\\uffff_-]{0,62}' +
    ')?' +
    '[a-z0-9\\u00a1-\\uffff]\\.' +
    ')+' +
    // TLD identifier name, may end with dot
    '(?:[a-z\\u00a1-\\uffff]{2,}\\.?)' +
    ')' +
    // port number (optional)
    '(?::\\d{2,5})?' +
    // resource path (optional)
    '(?:[/?#]\\S*)?' +
    '$',
  'i'
);

export function isUrl(text: string): boolean {
  return urlRegex.test(text?.trim());
}

/**
 * Zod currently doesn't support the Utf-8 extended regex for emails.
 * We manually define the regex here and use it in a custom email validation for zod.
 */
const emailRegexUtf8Extended = new RegExp(
  /^(?!.*[@]{2})(?!.*[.]{2})(?!.*[.@]$)[\p{L}\p{N}!#$%&'*+/=?^_{|}~-](?:[\p{L}\p{N}!#$%&'*+/=?^_{|}~.-]*[\p{L}\p{N}!#$%&'*+/=?^_{|}~-])?@[\p{L}\p{N}][\p{L}\p{N}-]*?(\.[\p{L}\p{N}-]+)*\.[\p{L}]{2,}$/u
);

/**
 * Check if the text provided is a valid email
 * Using the utf-8 extended regex for emails in a custom string validation for zod
 */
export function isEmail(text: string): boolean {
  const emailSchema = z.string().refine((value) => emailRegexUtf8Extended.test(value), {
    message: 'Invalid email',
  });

  return emailSchema.safeParse(text).success;
}

const urlResemblanceRegex = new RegExp('https?://', 'i');

/**
 * Checks if the text provided could become a URL, i.e. if it is a partial match on a valid URL
 * @param text A string you want to check could become a URL
 * @returns true if the text is a partially valid URL
 * @example https://some -> true
 * @example toast -> false
 */
export function resemblesUrl(text: string | undefined): boolean {
  if (!text) return false;
  return urlResemblanceRegex.test(text.trim());
}

/**
 * Checks if the text provided is a valid website
 * @param text A string you want to check is a valid website
 * returns message if false
 */
export function isValidWebsite(text: string): boolean | string {
  if (!text) return true;
  const parsed = parse(text);
  return parsed.domain ? true : false;
}

export function isValidPhone(text: string): boolean {
  if (!text) return true;
  const pattern = /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/;
  const trimmed = text.trim();
  if (trimmed.length < 3) return false;
  return pattern.test(text.trim());
}

const integrationErrorSchema = z.object({
  errorCode: z.string().nullish(),
  type: z.enum(['CRM', 'OTHERS']),
  reference: z.string().nullish(),
  customFieldsErrors: z.string().nullish(),
  pickListErrors: z.string().nullish(),
});

export type IntegrationError = z.infer<typeof integrationErrorSchema>;

/**
 * Check if the value is an integration error
 * @param value
 * @returns
 */
export function isIntegrationError(value: unknown): value is IntegrationError {
  return integrationErrorSchema.safeParse(value).success;
}
